import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useParams, useOutletContext } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';

import { execute } from '@/graphql/execute';
import { deviationJobsQuery } from '@/queries/deviation.query';
import { surfaceDifferenceJobsQuery } from '@/queries/surfaceDifference.query';
import { isSurfaceCandidate } from '@/components/RunSurfaceDifferenceDialog';
import { layersAtom } from '@/atoms/layers.atom';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import type { Project } from '@/graphql/graphql';

import { isDeviationReferenceCandidate } from '@/components/RunDeviationAnalysisDialog';
import { RunLayerStackDialog } from '@/components/RunLayerStackDialog';
import { Button } from '@/components/ui/button';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useDialog } from '@/providers/DialogProvider';
import { AnalysisRow } from './AnalysisRow';
import { SurfaceDifferenceRow } from './SurfaceDifferenceRow';
import { ANALYSIS_DIALOG_CLASS, ANALYSIS_DIALOG_OPTIONS } from '@/lib/analysisDialog';

export function AnalysesPage() {
  const { t } = useTranslation('project');
  const { projectId } = useParams<{ projectId: string }>();
  const allLayers = useAtomValue(layersAtom);

  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  usePageMeta({ title: [t('analyses.tabLabel'), project?.name] });

  const { data, isLoading } = useQuery({
    queryKey: ['deviationJobs', projectId],
    queryFn: () => execute(deviationJobsQuery, { projectId: projectId! }),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const jobs = query.state.data?.deviationJobs ?? [];
      const anyRunning = jobs.some(
        (j) => j.status === 'WAITING' || j.status === 'PROCESSING',
      );
      return anyRunning ? 2000 : false;
    },
  });

  const { data: sdData, isLoading: sdLoading } = useQuery({
    queryKey: ['surfaceDifferenceJobs', projectId],
    queryFn: () => execute(surfaceDifferenceJobsQuery, { projectId: projectId! }),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const jobs = query.state.data?.surfaceDifferenceJobs ?? [];
      const anyRunning = jobs.some(
        (j) => j.status === 'WAITING' || j.status === 'PROCESSING',
      );
      return anyRunning ? 2000 : false;
    },
  });

  const jobs = data?.deviationJobs ?? [];
  const sdJobs = sdData?.surfaceDifferenceJobs ?? [];
  const layersById = Object.fromEntries(allLayers.map((l) => [l.id, l]));
  const candidateReferenceLayers = allLayers.filter(isDeviationReferenceCandidate);
  const surfaceCandidateLayers = allLayers.filter(isSurfaceCandidate);

  const loading = isLoading || sdLoading;
  const bothEmpty = jobs.length === 0 && sdJobs.length === 0;

  /**
   * The second way into the multi-layer thickness analysis (ruling R17 gap 2
   * — the first is the layer drawer's context menu).
   *
   * A stack takes N surfaces rather than one source layer, so unlike the
   * deviation and surface-difference dialogs it has no natural "run this on
   * the layer I right-clicked" entry that covers every case: a user who wants
   * a five-level stack starts from the analyses page, not from one of its
   * five surfaces. Gated on two independent axes (Layers.tsx context-menu
   * entry :339 states the model): the PostHog `stack` flag controls whether
   * the button renders at all — flag off means invisible, not a paywall —
   * and the subscription gate is layered on top in the handler once it's
   * visible, so the button stays clickable and the click is what explains
   * the paywall.
   */
  const dialog = useDialog();
  const featureCheck = useFeatureCheck();
  const isLayerStackEnabled = !!useFeatureFlagEnabled('stack');
  const openLayerStack = () => {
    const gate = featureCheck('analysis.layerStack');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(
      <RunLayerStackDialog projectId={projectId!} candidateLayers={surfaceCandidateLayers} />,
      ANALYSIS_DIALOG_CLASS,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : project?.name ?? '', href: href ?? '' },
        { label: t('analyses.tabLabel'), href: `${href}/analyses` },
      ]}
    >
      <div className='max-w-screen-md mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-4'>
        {isLayerStackEnabled && (
          <div className='flex justify-end'>
            <Button
              data-testid='analyses-new-layer-stack'
              type='button'
              size='sm'
              variant='outline'
              disabled={surfaceCandidateLayers.length === 0}
              onClick={openLayerStack}>
              {t('layerStack.dialog.newRun')}
            </Button>
          </div>
        )}
        {loading ? (
          <div className='border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-slate-600 dark:text-stone-400'>
              <Loader2 className='size-4 animate-spin' />
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          </div>
        ) : bothEmpty ? (
          <div className='border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
            <div className='flex flex-col items-center justify-center gap-1 py-8 px-4 text-center'>
              <span className='text-slate-500 dark:text-stone-200 text-sm font-medium'>
                {t('analyses.empty')}
              </span>
              <span className='text-slate-500 dark:text-stone-400 text-sm'>
                {t('surfaceDifference.analyses.empty')}
              </span>
            </div>
          </div>
        ) : (
          <>
            {jobs.length > 0 && (
              <section className='flex flex-col gap-2'>
                <h2 className='text-sm font-semibold text-slate-700 dark:text-stone-200'>
                  {t('analyses.deviationSectionTitle')}
                </h2>
                <div className='border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
                  {jobs.map((job) => (
                    <AnalysisRow
                      key={job.id}
                      job={job}
                      layersById={layersById}
                      candidateReferenceLayers={candidateReferenceLayers}
                      projectId={projectId!}
                    />
                  ))}
                </div>
              </section>
            )}
            {sdJobs.length > 0 && (
              <section className='flex flex-col gap-2'>
                <h2 className='text-sm font-semibold text-slate-700 dark:text-stone-200'>
                  {t('surfaceDifference.analyses.sectionTitle')}
                </h2>
                <div className='border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
                  {sdJobs.map((job) => (
                    <SurfaceDifferenceRow
                      key={job.id}
                      job={job}
                      candidateLayers={surfaceCandidateLayers}
                      projectId={projectId!}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </PageSkeleton>
  );
}
